import api from './api';
import { ENDPOINTS } from './endpoints';
import { setCookie } from '../utils/helpers';
import {
  extractLoginQuestionnaireFieldsFromEnvelope,
  mergeAuthEnvelopeForQuestionnaire,
} from './auth';

function toYyyyMmDd(dateInput) {
  if (!dateInput) return null;
  if (typeof dateInput === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) return dateInput;
    const parsed = new Date(dateInput);
    if (!isNaN(parsed)) {
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, '0');
      const d = String(parsed.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }
  const d = new Date(dateInput);
  if (isNaN(d)) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function normalizeGender(gender) {
  if (typeof gender !== 'string' || !gender) return null;
  return gender.charAt(0).toUpperCase() + gender.slice(1);
}

const PROFILE_ME_PATH = ENDPOINTS.PROFILE.ME;
const PROFILE_PERSONAL_PATH = ENDPOINTS.PROFILE.PERSONAL;
const PROFILE_ACTIVATE_BARCODE_PATH = ENDPOINTS.PROFILE.ACTIVATE_BARCODE;
const PROFILE_MARK_ACTIVATE_BARCODE_PATH = ENDPOINTS.PROFILE.MARK_ACTIVATE_BARCODE;
const PROFILE_MEDICAL_PATH = ENDPOINTS.PROFILE.MEDICAL;

function clampSectionProgressPercent(value) {
  if (!Number.isFinite(value)) return null;
  const pct = value > 0 && value <= 1 ? value * 100 : value;
  return Math.min(100, Math.max(0, Math.round(pct)));
}

/** Parse `section_progress` from /profile/me (number or object with percentage). */
export function parseSectionProgressFromProfileData(data) {
  if (!data || typeof data !== 'object') return null;

  const raw = data.section_progress ?? data.sectionProgress;
  if (raw == null || raw === '') return null;

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return clampSectionProgressPercent(raw);
  }

  if (typeof raw === 'string') {
    const n = Number(raw);
    return Number.isFinite(n) ? clampSectionProgressPercent(n) : null;
  }

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return clampSectionProgressPercent(
      Number(raw.percentage ?? raw.percent ?? raw.progress ?? raw.completion_percentage)
    );
  }

  return null;
}

function parseSectionsCompletedFromProfileData(data) {
  if (!data || typeof data !== 'object') return null;
  const raw =
    data.section_completed ??
    data.sectionCompleted ??
    data.sections_completed ??
    data.sectionsCompleted;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : null;
}

export function extractQuestionnaireSummaryFromProfileData(data) {
  if (!data || typeof data !== 'object') {
    return {
      totalSections: null,
      totalCompletedSections: null,
      totalIncompleteSections: null,
      completedSectionsPercentage: null,
      section_progress: null,
      sections_completed: null,
      overallStatus: null,
    };
  }
  const totalSections = Number(data.total_sections);
  const totalCompletedSections = Number(data.total_completed_sections);
  const totalIncompleteSections = Number(data.total_incomplete_sections);
  const completedSectionsPercentage = Number(data.completed_sections_percentage);
  return {
    totalSections: Number.isFinite(totalSections) ? totalSections : null,
    totalCompletedSections: Number.isFinite(totalCompletedSections)
      ? totalCompletedSections
      : null,
    totalIncompleteSections: Number.isFinite(totalIncompleteSections)
      ? totalIncompleteSections
      : null,
    completedSectionsPercentage: Number.isFinite(completedSectionsPercentage)
      ? completedSectionsPercentage
      : null,
    section_progress: parseSectionProgressFromProfileData(data),
    sections_completed: parseSectionsCompletedFromProfileData(data),
    overallStatus:
      data.overall_status != null && String(data.overall_status).trim() !== ''
        ? String(data.overall_status).trim()
        : null,
  };
}

export async function fetchMyProfile() {
  const { data: envelope } = await api.get(PROFILE_ME_PATH);
  const merged = mergeAuthEnvelopeForQuestionnaire(envelope || {});

  const {
    full_name: fullName,
    email,
    gender,
    date_of_birth: dateOfBirth,
    role: role,
    organization: organization,
    street_number: streetNumber,
    street_name: streetName,
    postal_code: postalCode,
    city: city,
    telephone_number: telephoneNumber,
  } = merged;

  const qFields = extractLoginQuestionnaireFieldsFromEnvelope(envelope || {});
  const questionnaireSummary = extractQuestionnaireSummaryFromProfileData(merged);

  return {
    fullName: fullName || '',
    email: email || '',
    gender: gender || '',
    dateOfBirth: dateOfBirth || null,
    role: role || '',
    organization: organization || '',
    streetNumber: streetNumber || '',
    streetName: streetName || '',
    postalCode: postalCode || '',
    city: city || '',
    telephoneNumber: telephoneNumber || '',
    medical_questionnaire_completed: qFields.medical_questionnaire_completed,
    number_of_questions_filled: qFields.number_of_questions_filled,
    questionnaire_completion_percentage: qFields.questionnaire_completion_percentage,
    ...questionnaireSummary,
  };
}

export async function updateMyProfile(partial) {
  const payload = {};
  if (partial.fullName !== undefined) payload.full_name = partial.fullName;
  if (partial.email !== undefined) payload.email = partial.email;
  if (partial.gender !== undefined)
    payload.gender = normalizeGender(partial.gender);
  if (partial.dateOfBirth !== undefined)
    payload.date_of_birth = toYyyyMmDd(partial.dateOfBirth);
  if (partial.password !== undefined) payload.password = partial.password;
  if (partial.streetNumber !== undefined) payload.street_number = partial.streetNumber;
  if (partial.streetName !== undefined) payload.street_name = partial.streetName;
  if (partial.postalCode !== undefined) payload.postal_code = partial.postalCode;
  if (partial.city !== undefined) payload.city = partial.city;
  if (partial.telephoneNumber !== undefined) payload.telephone_number = partial.telephoneNumber;
  if (partial.must_change_password !== undefined) {
    payload.must_change_password = Boolean(partial.must_change_password);
  }

  const { data: envelope } = await api.patch(PROFILE_ME_PATH, payload);
  const merged = mergeAuthEnvelopeForQuestionnaire(envelope || {});

  const {
    full_name: fullName,
    email,
    gender,
    date_of_birth: dateOfBirth,
    role: role,
    organization: organization,
    street_number: streetNumber,
    street_name: streetName,
    postal_code: postalCode,
    city: city,
    telephone_number: telephoneNumber,
    access,
    refresh,
  } = merged;

  if (access) setCookie('access', access, { days: 1 });
  if (refresh) setCookie('refresh', refresh, { days: 7 });

  const questionnaireSummary = extractQuestionnaireSummaryFromProfileData(merged);

  return {
    fullName: fullName || '',
    email: email || '',
    gender: gender || '',
    dateOfBirth: dateOfBirth || null,
    role: role || '',
    organization: organization || '',
    streetNumber: streetNumber || '',
    streetName: streetName || '',
    postalCode: postalCode || '',
    city: city || '',
    telephoneNumber: telephoneNumber || '',
    ...questionnaireSummary,
  };
}

/**
 * Update personal details (onboarding step)
 * @param {Object} payload - { gender, date_of_birth (YYYY-MM-DD), street_number, street_name, postal_code, city, telephone_number }
 * @returns {Promise<Object>} Response with data including on_boarding_step
 */
export async function updatePersonalDetails(payload) {
  const { data: envelope } = await api.patch(PROFILE_PERSONAL_PATH, payload);
  if (!envelope || !envelope.success) {
    throw new Error(envelope?.message || envelope?.errors?.[0] || 'Failed to update personal details');
  }
  return envelope.data || envelope;
}

/**
 * Activate barcode (onboarding step)
 * POST api/v1/profile/barcode-activate
 * Supports legacy payload { barcode } and extended payload:
 * { blood_test_id, barcode_text, first_name, last_name, email }
 * @param {string|Object} payloadOrBarcode
 * @param {Object} [options]
 * @param {boolean} [options.onboarding]
 * @returns {Promise<Object>} Response data: { detail, on_boarding_step }
 */
export async function activateBarcode(payloadOrBarcode, options = {}) {
  let payload = {};
  if (typeof payloadOrBarcode === 'string' || typeof payloadOrBarcode === 'number') {
    payload = {
      barcode: String(payloadOrBarcode).trim(),
    };
  } else {
    const source = payloadOrBarcode || {};
    payload = {
      blood_test_id: String(
        source.blood_test_id ??
          source.bloodtestid ??
          source.bloodTestId ??
          source.recordId ??
          ''
      ).trim(),
      barcode_text: String(
        source.barcode_text ?? source.barcodeText ?? source.barcode ?? ''
      ).trim(),
      first_name: String(source.first_name ?? source.firstName ?? '').trim(),
      last_name: String(source.last_name ?? source.lastName ?? '').trim(),
      email: String(source.email ?? '').trim(),
    };
  }

  const requestConfig = {};
  if (options?.onboarding) {
    requestConfig.params = { onboarding: true };
  }

  const { data: envelope } = await api.post(PROFILE_ACTIVATE_BARCODE_PATH, payload, requestConfig);
  if (!envelope || envelope.success === false) {
    throw new Error(envelope?.message || envelope?.errors?.[0] || 'Failed to activate barcode');
  }
  return envelope.data || envelope;
}

/**
 * Mark activate barcode (embedded onboarding flow)
 * POST api/v1/profile/barcode-mark-activate
 * Payload: { barcode }
 * @param {string|Object} payloadOrBarcode
 * @returns {Promise<Object>} Response data
 */
export async function markActivateBarcode(payloadOrBarcode) {
  const isPrimitive =
    typeof payloadOrBarcode === 'string' || typeof payloadOrBarcode === 'number';
  const barcode = isPrimitive
    ? String(payloadOrBarcode).trim()
    : String(
        payloadOrBarcode?.barcode ??
          payloadOrBarcode?.barcode_text ??
          payloadOrBarcode?.barcodeText ??
          ''
      ).trim();
  const skip = !isPrimitive && payloadOrBarcode?.skip === true;

  const payload = {};
  if (barcode) payload.barcode = barcode;
  if (skip) payload.skip = true;

  const { data: envelope } = await api.post(PROFILE_MARK_ACTIVATE_BARCODE_PATH, payload);
  if (!envelope || envelope.success === false) {
    throw new Error(envelope?.message || envelope?.errors?.[0] || 'Failed to mark barcode activation');
  }
  return envelope.data || envelope;
}

/**
 * Update medical info (onboarding step)
 * PATCH api/v1/profile/me/medical
 * @param {Object} payload - Medical info fields
 * @returns {Promise<Object>} Response data including on_boarding_step
 */
export async function updateMedicalInfo(payload) {
  const { data: envelope } = await api.patch(PROFILE_MEDICAL_PATH, payload);
  if (!envelope || envelope.success === false) {
    throw new Error(envelope?.message || envelope?.errors?.[0] || 'Failed to save medical info');
  }
  return envelope.data || envelope;
}

export default {
  fetchMyProfile,
  updateMyProfile,
  updatePersonalDetails,
  activateBarcode,
  markActivateBarcode,
  updateMedicalInfo,
};
